import { Component, Input, OnInit } from '@angular/core';
import { Router, NavigationEnd, NavigationExtras } from '@angular/router';
import { AlertController, PopoverController, ModalController } from '@ionic/angular';
import { AuthSessionService } from '@app/core/services/auth-session.service';
import { UtilService } from '@app/core/services/util.service';
import { MessageConfig } from '@app/shared/constants/constant';
import { FirebaseDataService } from '@app/shared/services/firebase-data.service';
import { CommonService } from '@app/shared/services/common.service';
import { UserProfileService } from '@app/profile-setup/user-profile.service';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AppComponent } from '@app/app.component';
import { SigninService } from '@app/authentication/services/signin.service';
import { NavProfileMenuComponent } from './nav-popover/nav-popover';
import { UserProfileData } from '@app/profile-setup/profile.model';

@Component({
    selector: 'app-charter-nav',
    templateUrl: './charter-nav.component.html',
    styleUrls: ['./charter-nav.component.scss'],
    standalone: false
})
export class CharterNavComponent implements OnInit {

  @Input() public userType: any;
  @Input() public userName: string;
  @Input() public userEmail: string;
  @Input() public userLogo: string;
  @Input() public hideMenuButton: boolean = false;

  public messageDisplay = MessageConfig;
  public isLoggedIn: boolean = false;
  public showProfileMenu: boolean = false;
  public activeRoute: string = '';
  public isHomePage: boolean = false;
  private subscription: Subscription;
  public userProfileData = new UserProfileData();

  constructor(
    private router: Router,
    private authSessionService: AuthSessionService,
    private utilService: UtilService,
    private fbDatabase: FirebaseDataService,
    public commonService: CommonService,
    private alertCtrl: AlertController,
    private userProfileService: UserProfileService,
    private signinService: SigninService,
    private popoverCtrl: PopoverController,
    private modalCtrl: ModalController,
    private appComponent: AppComponent
  ) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.setActiveRoute(event.urlAfterRedirects);
      });
  }

  public ngOnInit() {
    this.checkLoginStatus();
    this.setActiveRoute(this.router.url);
    this.subscription = this.userProfileService.data$.subscribe((userData) => {
      if (userData) {
        this.updateUserInfo(userData);
      }
    });

    this.commonService.userProfileData$.subscribe((profileData: UserProfileData) => {
      if (profileData && profileData.Image && this.isLoggedIn && profileData.UserType) {
        if (this.userType === profileData.UserType) {
          this.userLogo = profileData.Image;
          this.userProfileData = profileData;
        }
      }
    });
  }

  public isIOS(): boolean {
    return this.commonService.platformTypeCheck === 'ios';
  }

  public ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  public checkLoginStatus() {
    this.authSessionService.$userSession.subscribe((res) => {
      this.isLoggedIn = !!res;
      if (res) {
        this.updateUserInfo(res);
      }
    });
  }

  public updateUserInfo(res: any) {
    this.userType = res.RoleID;
    this.userLogo = this.messageDisplay.NoImageAvailable;
    if (!res) return;
    this.userType = res.RoleID;
    switch (res.RoleID) {
      case 2:
        this.userName = res.clientDetails.ClientName;
        this.userLogo = res.clientDetails.logo || this.messageDisplay.NoImageAvailable;
        this.userEmail = res.clientDetails.CompanyEmail;
        break;
      case 3:
        this.userName = res.vendorDetails.VendorName;
        this.userLogo = res.vendorDetails.logo || this.messageDisplay.NoImageAvailable;
        this.userEmail = res.vendorDetails.CompanyEmail;
        break;
      case 7:
        this.userName = res.charterDetails.CompanyName;
        this.userLogo = res.charterDetails.photoUrl ||
                        res.charterDetails.logo ||
                        this.messageDisplay.NoImageAvailable;
        this.userEmail = res.charterDetails.CompanyEmail;
        break;
    }
  }

  public onProfileImageError(event: Event) {
    const image = event.target as HTMLImageElement;
    if (image.src.endsWith(this.messageDisplay.NoImageAvailable)) {
      return;
    }
    image.src = this.messageDisplay.NoImageAvailable;
  }

  // public async presentPopover(ev: any) {
  //   const popover = await this.popoverCtrl.create({
  //     component: NavProfileMenuComponent,
  //     event: ev,
  //     translucent: true,
  //     componentProps: {
  //       userType: this.userType
  //     }
  //   });
  //   return await popover.present();
  // }

  public async presentModal(ev: any) {
    const modal = await this.modalCtrl.create({
      component: NavProfileMenuComponent,
      componentProps: {
        userType: this.userType
      },
      cssClass: 'profile-modal',
      presentingElement: await this.modalCtrl.getTop()
    });
    return await modal.present();
  }

  public navigateTo(route: string) {
    const navigationExtras = {
      state: {
        userType: this.userType,
      },
    };
    this.router.navigate([`/${route}`], navigationExtras);
  }

  public navigateToHome() {
    const navigationExtras = {
      state: {
        PageType: 'charterNewTrip',
        userType: this.userType
      }
    };
    this.router.navigate(['charter/charter-create-schedule'], navigationExtras);
  }

  public profile() {
    this.showProfileMenu = false;
    this.router.navigateByUrl('profile-setup');
  }

  public showWalletBalance() {
    this.showProfileMenu = false;
    this.appComponent.hideMenuOnDesktop();
    const navigationExtras: NavigationExtras = {
      state: {
        userType: this.userType,
      },
    };
    this.router.navigate(['/wallet/'], navigationExtras);
  }

  public support() {
    this.showProfileMenu = false;
    this.fbDatabase.supportLink().then((data: any) => {
      const ref = window.open(data, '_blank');
    });
  }

  public async onlyLogOut() {
    this.showProfileMenu = false;
    const alert = await this.alertCtrl.create({
      header: 'Logout!',
      subHeader: 'You want to Logout?',
      buttons: [
        {
          text: 'No',
          handler: () => {
            alert.dismiss();
            return false;
          },
        },
        {
          text: 'Yes',
          handler: () => {
            this.utilService.showSpinnerLoader();
            this.userLogo = this.messageDisplay.NoImageAvailable;
            this.userProfileData = new UserProfileData();
            this.userType = null;
            this.signinService.sendLogout();
            this.authSessionService.logOut();
          },
        },
      ],
      backdropDismiss: false,
    });
    await alert.present();
  }

  public signIn() {
    this.router.navigateByUrl('/authentication');
  }

  public toggleProfileMenu() {
    this.showProfileMenu = !this.showProfileMenu;
  }

  private setActiveRoute(url: string): void {
    if (url.includes('/charter-create-schedule') || url === '/') {
      this.activeRoute = 'home';
      this.isHomePage = true;
      this.authSessionService.activeRoute.next('home');
    } else if (url.includes('/save-estimate') || url.includes('/estimates')) {
      this.activeRoute = 'estimates';
      this.isHomePage = false;
    } else if (url.includes('/charter-estimate')) {
      this.isHomePage = false;
    } else if (url.includes('/charter') && !url.includes('/charter-invoice') && !url.includes('/save-estimate')) {
      this.activeRoute = 'trips';
      this.isHomePage = false;
    } else if (url.includes('/charter-invoice') || url.includes('/invoices')) {
      this.activeRoute = 'invoices';
      this.isHomePage = false;
    } else if (url.includes('/wallet')) {
      this.activeRoute = 'wallet';
      this.isHomePage = false;
    } else if (url.includes('/setup')) {
      this.activeRoute = 'setup';
      this.isHomePage = false;
    } else {
      this.activeRoute = '';
      this.isHomePage = false;
    }
  }
}